/**
 * useSaveDealOrchestrator - Unified Save Orchestrator for Deals
 *
 * Phase 4 of the Deals Flow Refactoring
 *
 * This hook consolidates save operations for deals, products, and related entities
 * into a single orchestrated flow with:
 * - Transaction-like behavior (ordered steps with proper dependencies)
 * - Error handling with partial rollback
 * - Progress tracking for UI feedback
 * - Consistent cache updates
 *
 * @see docs/DEALS_REFACTORING.md for full context
 */

import { SEARCH_DATA_KEY } from 'api/search'
import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/organizations_table/useBatchUpdateItems'
import { useSyncProducts } from 'api/aws/organizations_table/useSyncProducts'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { dedupeAndNormalizeProducts, sanitizeForSync } from 'context/DealsDetailsProvider/normalizeDeal'
import { calculateDealTotals } from 'utils/dealCalculations'
import type { NewDeal, Product, DealUpdate, ProductChanges } from 'types'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type SaveStep =
  | 'idle'
  | 'validating'
  | 'saving-recipients'
  | 'saving-deal'
  | 'saving-products'
  | 'updating-pandadoc'
  | 'updating-cache'
  | 'complete'
  | 'error'

export interface SaveProgress {
  step: SaveStep
  message: string
  percent: number
}

export interface SaveResult {
  ok: boolean
  deal?: NewDeal
  products?: Product[]
  syncResp?: SyncResp
  error?: Error
  /** Steps that completed before failure (for partial rollback) */
  completedSteps: SaveStep[]
}

export interface OrchestratorSaveArgs {
  deal: NewDeal
  dealPK: string
  dealSK: string
  deal_uuid: string

  /** Deal field updates to persist */
  dealUpdates: DealUpdate[]

  /** Current product rows (used for totals calculation + full sync mode) */
  currentProducts: Product[]

  /** Diff-based product changes (created/updated/deleted) */
  productChanges: ProductChanges

  /** Set true to send all products instead of just the diff */
  sendAllProductsInsteadOfDiff?: boolean

  /** Optional callbacks for recipient reassignment */
  recipientsChanged?: boolean
  onReassignAuth?: () => Promise<any>
  onReassignClient?: () => Promise<any>

  /** Optional callback for timeline entry creation */
  onTimeline?: () => Promise<any>

  /** Optional PandaDoc update callback */
  onPandaDocUpdate?: (_savedDeal: NewDeal, _savedProducts: Product[]) => Promise<void>

  /** Skip snackbar notifications */
  silent?: boolean

  /** User ID for version tracking (who made this change) */
  userId?: string
}

type SyncResp = {
  message?: string
  added?: number
  updated?: number
  deleted?: number
  upserts?: Product[]
  deletes?: { PK: string; SK: string }[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ALL_ITEMS_KEY = SEARCH_DATA_KEY
const EMPTY_SYNC: SyncResp = { upserts: [], deletes: [] }

const STEP_PROGRESS: Record<SaveStep, { message: string; percent: number }> = {
  idle: { message: '', percent: 0 },
  validating: { message: 'Validating changes...', percent: 10 },
  'saving-recipients': { message: 'Updating recipients...', percent: 25 },
  'saving-deal': { message: 'Saving deal...', percent: 40 },
  'saving-products': { message: 'Syncing products...', percent: 60 },
  'updating-pandadoc': { message: 'Updating contract...', percent: 80 },
  'updating-cache': { message: 'Updating local data...', percent: 90 },
  complete: { message: 'Saved successfully', percent: 100 },
  error: { message: 'Save failed', percent: 0 },
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useSaveDealOrchestrator() {
  const qc = useQueryClient()
  const { showSnackbar } = useSnackbarContext()
  const batchUpdate = useBatchUpdateItems()
  const syncProducts = useSyncProducts()

  // Progress state for UI feedback
  const [progress, setProgress] = useState<SaveProgress>({
    step: 'idle',
    message: '',
    percent: 0,
  })

  // Track if a save is in progress
  const isSavingRef = useRef(false)

  // Track completed steps for potential rollback
  const completedStepsRef = useRef<SaveStep[]>([])

  /**
   * Update progress state
   */
  const updateProgress = useCallback((step: SaveStep) => {
    const { message, percent } = STEP_PROGRESS[step]
    setProgress({ step, message, percent })
  }, [])

  /**
   * Main orchestrated save function
   *
   * Execution order:
   * 1. Validate inputs
   * 2. Handle recipient changes (if any)
   * 3. Save deal field updates
   * 4. Sync products (upserts + deletes)
   * 5. Update PandaDoc (if callback provided)
   * 6. Update React Query cache
   * 7. Create timeline entry (if callback provided)
   */
  const save = useCallback(
    async (args: OrchestratorSaveArgs): Promise<SaveResult> => {
      const {
        deal,
        dealPK,
        dealSK,
        deal_uuid,
        dealUpdates,
        currentProducts,
        productChanges,
        sendAllProductsInsteadOfDiff = false,
        recipientsChanged = false,
        onReassignAuth,
        onReassignClient,
        onTimeline,
        onPandaDocUpdate,
        silent = false,
        userId,
      } = args

      // ─────────────────────────────────────────────────────────────────────
      // Version tracking: increment version on each save
      // ─────────────────────────────────────────────────────────────────────
      const currentVersion = deal._version ?? 0
      const newVersion = currentVersion + 1
      const versionTimestamp = new Date().toISOString()

      // Prevent concurrent saves
      if (isSavingRef.current) {
        console.warn('[useSaveDealOrchestrator] Save already in progress, skipping')
        return {
          ok: false,
          error: new Error('Save already in progress'),
          completedSteps: [],
        }
      }

      isSavingRef.current = true
      completedStepsRef.current = []

      try {
        // ─────────────────────────────────────────────────────────────────────
        // Step 1: Validate
        // ─────────────────────────────────────────────────────────────────────
        updateProgress('validating')

        if (!dealPK || !dealSK) {
          throw new Error('Missing deal PK/SK')
        }

        completedStepsRef.current.push('validating')

        // ─────────────────────────────────────────────────────────────────────
        // Step 2: Recipients (if changed)
        // ─────────────────────────────────────────────────────────────────────
        if (recipientsChanged && (onReassignAuth || onReassignClient)) {
          updateProgress('saving-recipients')

          const recipientPromises = [onReassignAuth?.(), onReassignClient?.()].filter(
            Boolean
          ) as Promise<any>[]

          if (recipientPromises.length > 0) {
            const results = await Promise.allSettled(recipientPromises)
            const failures = results.filter(r => r.status === 'rejected')
            if (failures.length > 0) {
              console.warn('[useSaveDealOrchestrator] Some recipient updates failed:', failures)
              // Continue anyway - recipient changes are not critical
            }
          }

          completedStepsRef.current.push('saving-recipients')
        }

        // ─────────────────────────────────────────────────────────────────────
        // Step 3: Save deal fields
        // ─────────────────────────────────────────────────────────────────────
        updateProgress('saving-deal')

        // Calculate totals from current products
        const calculatedTotals = calculateDealTotals(deal, currentProducts)

        // Build final updates array, ensuring totals and version tracking is included
        const hasTotalsUpdate = dealUpdates.some(u => u.fieldName === 'totals')
        const baseUpdates: DealUpdate[] = hasTotalsUpdate
          ? dealUpdates.map(u =>
              u.fieldName === 'totals'
                ? { ...u, newValue: calculatedTotals }
                : u
            )
          : [
              ...dealUpdates,
              {
                tableName: 'ORGANIZATIONS_TABLE',
                PK: dealPK,
                SK: dealSK,
                fieldName: 'totals',
                newValue: calculatedTotals,
              },
            ]

        // Add version tracking fields
        const finalDealUpdates: DealUpdate[] = [
          ...baseUpdates,
          {
            tableName: 'ORGANIZATIONS_TABLE',
            PK: dealPK,
            SK: dealSK,
            fieldName: '_version',
            newValue: newVersion,
          },
          {
            tableName: 'ORGANIZATIONS_TABLE',
            PK: dealPK,
            SK: dealSK,
            fieldName: '_lastModifiedAt',
            newValue: versionTimestamp,
          },
          ...(userId
            ? [
                {
                  tableName: 'ORGANIZATIONS_TABLE',
                  PK: dealPK,
                  SK: dealSK,
                  fieldName: '_lastModifiedBy',
                  newValue: userId,
                },
              ]
            : []),
        ]

        console.log(`[useSaveDealOrchestrator] Saving deal v${currentVersion} -> v${newVersion}`)

        if (finalDealUpdates.length > 0) {
          await batchUpdate.mutateAsync(finalDealUpdates)
        }

        completedStepsRef.current.push('saving-deal')

        // ─────────────────────────────────────────────────────────────────────
        // Step 4: Sync products
        // ─────────────────────────────────────────────────────────────────────
        updateProgress('saving-products')

        const productsToUpsert = (
          sendAllProductsInsteadOfDiff
            ? currentProducts
            : [...productChanges.created, ...productChanges.updated]
        ).map(sanitizeForSync)

        const shouldSyncProducts =
          productsToUpsert.length > 0 || productChanges.deletedIds.length > 0

        let syncResp: SyncResp = EMPTY_SYNC

        if (shouldSyncProducts) {
          syncResp = await syncProducts.mutateAsync({
            tableName: 'ORGANIZATIONS_TABLE',
            PK: dealPK,
            deal_uuid,
            products: productsToUpsert,
            deletedProducts: productChanges.deletedIds,
          })
        }

        completedStepsRef.current.push('saving-products')

        // Build final saved products list
        const productsByKey = new Map(currentProducts.map(p => [`${p.PK}|${p.SK}`, p]))
        for (const d of syncResp.deletes ?? []) productsByKey.delete(`${d.PK}|${d.SK}`)
        for (const u of syncResp.upserts ?? []) productsByKey.set(`${u.PK}|${u.SK}`, u)
        const finalProducts = dedupeAndNormalizeProducts([...productsByKey.values()])

        // Build saved deal object with version tracking
        const savedDeal: NewDeal = {
          ...deal,
          totals: calculatedTotals,
          _version: newVersion,
          _lastModifiedAt: versionTimestamp,
          ...(userId ? { _lastModifiedBy: userId } : {}),
        }

        // ─────────────────────────────────────────────────────────────────────
        // Step 5: PandaDoc update (optional)
        // ─────────────────────────────────────────────────────────────────────
        if (onPandaDocUpdate) {
          updateProgress('updating-pandadoc')
          try {
            await onPandaDocUpdate(savedDeal, finalProducts)
          } catch (pandaError) {
            console.error('[useSaveDealOrchestrator] PandaDoc update failed:', pandaError)
            // Don't fail the whole save for PandaDoc issues
            if (!silent) {
              showSnackbar('Deal saved but contract update failed', 'warning')
            }
          }
          completedStepsRef.current.push('updating-pandadoc')
        }

        // ─────────────────────────────────────────────────────────────────────
        // Step 6: Update cache
        // ─────────────────────────────────────────────────────────────────────
        updateProgress('updating-cache')

        // Update the main allItems cache
        qc.setQueryData(ALL_ITEMS_KEY, (prev: any) => {
          if (!prev) return prev
          const next = { ...prev }

          // Update deal in cache
          if (Array.isArray(next.deals)) {
            next.deals = next.deals.map((d: any) =>
              d.PK === dealPK && d.SK === dealSK
                ? {
                    ...d,
                    ...savedDeal,
                    hasProducts: finalProducts.length > 0,
                  }
                : d
            )
          }

          // Update products in cache
          if (Array.isArray(next.products)) {
            const byKey = new Map(next.products.map((p: Product) => [`${p.PK}|${p.SK}`, p]))

            // Remove deleted products
            for (const d of syncResp.deletes ?? []) byKey.delete(`${d.PK}|${d.SK}`)
            // Upsert products
            for (const u of syncResp.upserts ?? []) byKey.set(`${u.PK}|${u.SK}`, u)

            next.products = dedupeAndNormalizeProducts([...byKey.values()])
          }

          return next
        })

        // Also update the individual item cache
        qc.setQueryData(['item', dealPK, dealSK], (old: any) => ({
          ...(old ?? {}),
          ...savedDeal,
        }))

        completedStepsRef.current.push('updating-cache')

        // ─────────────────────────────────────────────────────────────────────
        // Step 7: Timeline (optional, fire and forget)
        // ─────────────────────────────────────────────────────────────────────
        if (onTimeline) {
          // Don't await - timeline is not critical
          onTimeline().catch(err =>
            console.warn('[useSaveDealOrchestrator] Timeline entry failed:', err)
          )
        }

        // ─────────────────────────────────────────────────────────────────────
        // Complete
        // ─────────────────────────────────────────────────────────────────────
        updateProgress('complete')

        if (!silent) {
          const productCount =
            (syncResp.added ?? 0) + (syncResp.updated ?? 0) + (syncResp.deleted ?? 0)
          const message = productCount > 0 ? `Deal and ${productCount} products saved` : 'Deal saved'
          showSnackbar(message, 'success')
        }

        return {
          ok: true,
          deal: savedDeal,
          products: finalProducts,
          syncResp,
          completedSteps: completedStepsRef.current,
        }
      } catch (error) {
        console.error('[useSaveDealOrchestrator] Save failed:', error)
        updateProgress('error')

        if (!silent) {
          showSnackbar('Failed to save deal', 'error')
        }

        return {
          ok: false,
          error: error instanceof Error ? error : new Error(String(error)),
          completedSteps: completedStepsRef.current,
        }
      } finally {
        isSavingRef.current = false

        // Reset progress after a short delay
        setTimeout(() => {
          setProgress({ step: 'idle', message: '', percent: 0 })
        }, 2000)
      }
    },
    [qc, batchUpdate, syncProducts, showSnackbar, updateProgress]
  )

  /**
   * Check if a save is currently in progress
   */
  const isSaving = progress.step !== 'idle' && progress.step !== 'complete' && progress.step !== 'error'

  /**
   * Reset progress state (useful for cleanup)
   */
  const reset = useCallback(() => {
    setProgress({ step: 'idle', message: '', percent: 0 })
    isSavingRef.current = false
    completedStepsRef.current = []
  }, [])

  return {
    save,
    progress,
    isSaving,
    reset,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility: Quick save (simplified API for common cases)
// ─────────────────────────────────────────────────────────────────────────────

export interface QuickSaveArgs {
  deal: NewDeal
  products: Product[]
  productChanges: ProductChanges
  dealUpdates?: DealUpdate[]
}

/**
 * useDealQuickSave - Simplified save hook for common use cases
 *
 * Wraps useSaveDealOrchestrator with sensible defaults
 */
export function useDealQuickSave() {
  const orchestrator = useSaveDealOrchestrator()

  const quickSave = useCallback(
    async (args: QuickSaveArgs): Promise<SaveResult> => {
      const { deal, products, productChanges, dealUpdates = [] } = args

      if (!deal.PK || !deal.SK) {
        return {
          ok: false,
          error: new Error('Deal missing PK/SK'),
          completedSteps: [],
        }
      }

      const deal_uuid = deal.SK.startsWith('DEAL_') ? deal.SK.slice('DEAL_'.length) : (deal.deal_uuid ?? '')

      return orchestrator.save({
        deal,
        dealPK: deal.PK,
        dealSK: deal.SK,
        deal_uuid,
        dealUpdates,
        currentProducts: products,
        productChanges,
      })
    },
    [orchestrator]
  )

  return {
    quickSave,
    ...orchestrator,
  }
}
